import { useAuth } from '../../contexts/AuthContext';

// One derivation of workflow permissions (docs/workflow-ui.md §5).
// Submit keys split "new" from "amendment" on both sides. SUBMIT_KEY is the
// same table as server/routes/workflowGuards.js SUBMIT_PERM; a list means any
// one grants. The chosen-reviewer flows (client/car/engreg/ear/qc/qcad/fs/
// qc35/qca/fsa/qc35a) have NO review key: the server computes canReview per
// request (the reviewer named on it, who needs no key at all), so detail pages
// must always pass the server payload.
//
// For detail pages, pass the detail endpoint's payload: the server-computed
// canReview / canModify / canDelete flags win over the client-side guess.

// Engagement Audit (workflow:audit:*) is a superset of Engagement General
// (workflow:engagement:*): every flow General files, Audit files too, plus
// Signed Financial Statements / QC35 Form, which only Audit files.
const ENGAGEMENT = ['workflow:engagement:submit', 'workflow:audit:submit'];
const ENGAGEMENT_AMENDMENT = ['workflow:engagement:amendment:submit', 'workflow:audit:amendment:submit'];
export const SUBMIT_KEY = {
  client: 'workflow:client:submit',
  car: 'workflow:client:amendment:submit',
  engreg: ENGAGEMENT,
  qc: ENGAGEMENT,
  qcad: ENGAGEMENT,
  cwcc: ENGAGEMENT,
  fs: 'workflow:audit:submit',
  qc35: 'workflow:audit:submit',
  ear: ENGAGEMENT_AMENDMENT,
  qca: ENGAGEMENT_AMENDMENT,
  esc: ENGAGEMENT_AMENDMENT,
  fsa: 'workflow:audit:amendment:submit',
  qc35a: 'workflow:audit:amendment:submit',
  screening: 'workflow:screening:submit',
};
/** A flow's submit keys — always a list, any one grants. */
export const submitKeysOf = (flow) => [].concat(SUBMIT_KEY[flow] || []);

// Either Audit key shows the Signed Financial Statements / QC35 Form columns
// (and their dates) on My Records → Engagements.
export const AUDIT_KEYS = ['workflow:audit:submit', 'workflow:audit:amendment:submit'];
export const canSeeAuditStages = (hasPermission) => AUDIT_KEYS.some(hasPermission);

// 鑰匙審核的流程：ESC 判定一把、CWCC 審核一把、Screening 完成一把——與
// server/routes/workflowGuards.js 的 REVIEW_PERM 同一張表。CWCC 的 QM 執行段
// 另一把，見 COMPLETE_KEY。
export const REVIEW_KEY = {
  esc: 'workflow:esc:review',
  cwcc: 'workflow:cwcc:review',
  screening: 'workflow:screening:review',
};
const KEYED_REVIEW = Object.keys(REVIEW_KEY);
export const reviewKeyOf = (flow) => REVIEW_KEY[flow];

// 核准後還有執行段的流程——與 workflowGuards.js 的 COMPLETE_PERM 同一張表。
export const COMPLETE_KEY = {
  cwcc: 'workflow:cwcc:complete',
};
export const completeKeyOf = (flow) => COMPLETE_KEY[flow];

export default function useWorkflowPerms(flow, detail = null) {
  const { hasPermission } = useAuth();
  const canSubmit = submitKeysOf(flow).some(hasPermission);
  const canReview = KEYED_REVIEW.includes(flow) ? hasPermission(reviewKeyOf(flow)) : false;

  return {
    canSubmit,
    canReview: detail?.canReview !== undefined ? detail.canReview : canReview,
    canModify: detail?.canModify !== undefined ? detail.canModify : false,
    canDelete: detail?.canDelete !== undefined ? detail.canDelete : false,
  };
}
